// Shared co-located-pin fan-out helpers for the /places map marker builders.
//
// Distinct places sometimes share IDENTICAL (or sub-metre) coordinates — e.g.
// two shops in the same building ("미나리산장 가산점" and "샐러리아 가산점" both at
// 37.4745451,126.8897367). They are correctly NOT merged (different names), but
// stacked exactly on top of each other they render as one pin: only one label
// wins the symbol-collision, and a map click resolves to an arbitrary one of the
// overlapping features — so tapping the pin you SEE could select the OTHER place.
// To make each co-located place individually visible/tappable, we fan pins that
// share a coordinate cell out onto a tiny circle (a few metres) around the shared
// point. Untouched for the 99.9% of places that don't collide. This changes
// marker GEOMETRY only — place latitude/longitude and list distances are never
// touched. It has no effect on clustering: fanning is sub-10m and only becomes
// visible at high zoom where pins are already rendered individually.
//
// Shared by BOTH the desktop builder
// (src/app/(authenticated)/(users)/places/_desktop/place-results.js) and the
// mobile builder (…/places-mobile-secondary-screen/index.jsx → generateMarkers)
// so the two paths can't drift. Originally desktop commit 32ea785a7; extracted
// to this shared util per worklog-2026-07-27b (mobile port).

export const COLOCATED_COORD_PRECISION = 5; // ~1.1m grid cell
export const COLOCATED_SPREAD_METERS = 6; // ring radius for fanned-out pins
const METERS_PER_DEG_LAT = 111320;

export const getColocatedCoordKey = (place) => {
  const lat = Number(place?.latitude);
  const lng = Number(place?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return `${lat.toFixed(COLOCATED_COORD_PRECISION)},${lng.toFixed(
    COLOCATED_COORD_PRECISION
  )}`;
};

// Returns a map of coordinate-cell key -> total count, so the marker builder
// knows which places need fanning out and how many share the spot.
export const buildColocationGroups = (places) => {
  const groups = new Map();
  (places || []).forEach((place) => {
    const key = getColocatedCoordKey(place);
    if (!key) return;
    groups.set(key, (groups.get(key) || 0) + 1);
  });
  return groups;
};

export const getSpreadCoordinates = (place, groupSize, memberIndex) => {
  const lng = Number(place?.longitude);
  const baseLat = Number(place?.latitude);
  if (!Number.isFinite(baseLat) || !Number.isFinite(lng)) {
    return [lng, baseLat];
  }
  if (groupSize <= 1) return [lng, baseLat];

  const angle = (2 * Math.PI * memberIndex) / groupSize;
  const dLat = (COLOCATED_SPREAD_METERS / METERS_PER_DEG_LAT) * Math.sin(angle);
  const lngScale = Math.cos((baseLat * Math.PI) / 180) || 1;
  const dLng =
    (COLOCATED_SPREAD_METERS / (METERS_PER_DEG_LAT * lngScale)) *
    Math.cos(angle);
  return [lng + dLng, baseLat + dLat];
};
